/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, forwardRef } from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    NG_VALIDATORS,
    Validator,
    AbstractControl,
    ValidationErrors
} from '@angular/forms';

@Component({
    selector: 'sag-ui-input',
    template: `
        <div class="flex flex-col">
            <div class="flex flex-col-reverse w-full relative">
                <input
                    sag-ui-input
                    [id]="id"
                    [type]="mappedType"
                    [disabled]="disabled"
                    [required]="required"
                    [placeholder]="floatLabel ? '' : placeholder"
                    [value]="displayValue"
                    [readOnly]="readOnly"
                    [step]="step"
                    (input)="onInputChange($event)"
                    (click)="onClick($event)"
                    (focus)="onFocus($event)"
                    (blur)="onBlur($event)"
                    (keydown)="onKeyDown($event)"
                    (keyup)="keyup.emit($event)"
                    (mouseenter)="mouseenter.emit($event)"
                    (mouseleave)="mouseleave.emit($event)"
                    [attr.aria-label]="ariaLabel"
                    [attr.aria-labelledby]="ariaLabelledby || id"
                    [attr.aria-describedby]="ariaDescribedby"
                    [attr.data-float-label]="floatLabel"
                    [attr.data-invalid]="invalid || hasValidationErrors()"
                    class="[appearance:textfield] [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
                <label
                    sag-ui-input-label
                    [htmlFor]="id"
                    [attr.data-float]="floatLabel ? focused || !!value : undefined"
                    [attr.data-float-label]="floatLabel"
                >
                    <span *ngIf="required">*</span>
                    {{ label }}
                    <span
                        *ngIf="hint"
                        class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                        sag-ui-tooltip
                        [tooltip]="hint"
                    ></span>
                </label>
                <span
                    class="absolute left-0 bottom-0 h-[42px] flex justify-center items-baseline select-none pointer-events-none"
                >
                    <ng-content select="start-decorator"></ng-content>
                </span>
                <span
                    class="absolute right-0 bottom-0 h-[42px] flex justify-center items-baseline select-none pointer-events-none"
                >
                    <ng-content select="end-decorator"></ng-content>
                </span>
                <span
                    *ngIf="type === 'date'"
                    class="absolute right-4 bottom-[0.8rem] z-10 icon-calendar text-black dark:text-white"
                ></span>
                <div class="absolute top-full right-0 flex items-center z-20">
                    <sag-ui-date-picker
                        *ngIf="type === 'date' && showDatePicker"
                        [date]="dateValue!"
                        [time]="timeFormat"
                        [lang]="lang"
                        (dateChanged)="handleDateChange($event)"
                    ></sag-ui-date-picker>
                </div>
            </div>
            <div *ngIf="error && isArray(error)" class="flex flex-col gap-1">
                <span sag-ui-input-error *ngFor="let err of error">{{ err }}</span>
            </div>
            <span sag-ui-input-error *ngIf="error && !isArray(error)">{{ error }}</span>
        </div>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InputComponent),
            multi: true
        }
    ]
})
export class InputComponent implements ControlValueAccessor, Validator {
    @Input() id = this.randomId();
    @Input() disabled = false;
    @Input() required = false;
    @Input() invalid: boolean | null = null;
    @Input() error: string | string[] | null = null;
    @Input() label = '';
    @Input() value = '';
    @Input() placeholder = '';
    @Input() type = 'text';
    @Input() timeFormat?: 'hh:mm' | 'hh:mm:ss';
    @Input() lang?: 'en' | 'de';
    @Input() readOnly = false;
    @Input() hint?: string;
    @Input() floatLabel = false;
    @Input() step = 1;

    // Accessibility inputs
    @Input() ariaLabel: string | null = null;
    @Input() ariaLabelledby: string | null = null;
    @Input() ariaDescribedby: string | null = null;

    // Outputs for events
    @Output() change = new EventEmitter<Event>();
    @Output() input = new EventEmitter<Event>();
    @Output() click = new EventEmitter<Event>();
    @Output() focus = new EventEmitter<Event>();
    @Output() blur = new EventEmitter<Event>();
    @Output() keydown = new EventEmitter<KeyboardEvent>();
    @Output() keyup = new EventEmitter<KeyboardEvent>();
    @Output() mouseenter = new EventEmitter<MouseEvent>();
    @Output() mouseleave = new EventEmitter<MouseEvent>();
    @Output() dateChange = new EventEmitter<string>();

    @HostListener('document:mousedown', ['$event'])
    onMouseDown(event: MouseEvent): void {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            if (this.showDatePicker) {
                this.markAsTouched();
            }
            this.showDatePicker = false;
        }
    }

    @HostListener('focusout', ['$event'])
    onFocusOut(event: FocusEvent): void {
        const focusedInside = this.el.nativeElement.contains(event.target);
        if (!focusedInside) {
            this.showDatePicker = false;
            this.markAsTouched();
        }
    }

    constructor(private el: ElementRef) {}

    protected dateValue: Date | null = null;
    protected showDatePicker = false;
    protected focused = false;

    protected handleDateChange(event: { value: Date; emit: boolean }): void {
        this.dateValue = event.value;
        this.value = event.value.toISOString();
        this.onChangeEvent(this.value);
        this.dateChange.emit(this.value);

        if (event.emit) {
            setTimeout(() => {
                this.showDatePicker = false;
                this.el.nativeElement.blur();
            }, 100);
        }
    }

    touched = false;
    onChangeEvent = (_: any) => {};
    onTouched = () => {};

    writeValue(obj: any): void {
        this.value = obj;
        if (this.type === 'date') {
            this.dateValue = obj === null ? null : new Date(obj);
        }
    }

    registerOnChange(fn: any): void {
        this.onChangeEvent = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    onClick(event: Event): void {
        event.stopPropagation();
        this.click.emit(event);
        if (this.type === 'date') {
            this.showDatePicker = true;
        }
    }

    onFocus(event: Event): void {
        event.stopPropagation();
        this.focus.emit(event);
        this.focused = true;
        if (this.type === 'date') {
            this.showDatePicker = true;
        }
    }

    onBlur(event: Event): void {
        event.stopPropagation();
        this.focused = false;

        if (this.type === 'date' && !this.showDatePicker) {
            setTimeout(() => {
                this.markAsTouched();
            }, 100);
        }

        this.blur.emit(event);
    }

    onKeyDown(event: KeyboardEvent): void {
        if (this.type === 'number') {
            if (['e', 'E', '+', '-'].includes(event.key)) {
                event.preventDefault();
            }
        }

        this.keydown.emit(event);
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onInputChange(event: Event): void {
        event.stopPropagation();
        event.preventDefault();

        // Block input for date type
        if (this.type === 'date') {
            if (event.target && (event.target as any)['value']) {
                (event.target as any)['value'] = this.displayValue;
            }
            return;
        }

        const input = event.target as HTMLInputElement;
        this.value = input.value;
        this.onChangeEvent(this.value);
        this.input.emit(event);
    }

    validate(): ValidationErrors | null {
        if (this.type === 'date' && this.value && !this.isDate(this.value)) {
            return { invalidDate: true };
        }
        return this.required && !this.value ? { required: true } : null;
    }

    hasValidationErrors(): boolean {
        return this.validate() !== null && this.touched;
    }

    isArray(value: string | string[]): value is string[] {
        return Array.isArray(value);
    }

    private markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    private randomId(): string {
        return Math.random().toString(36).substring(2);
    }

    protected get mappedType(): string {
        switch (this.type) {
            case 'date':
            case 'datetime':
                return 'text';
            default:
                return this.type;
        }
    }

    protected get displayValue(): string {
        if (this.type === 'date' && this.dateValue && this.timeFormat) {
            if (this.timeFormat === 'hh:mm') {
                return `${this.dateValue.toLocaleDateString()} ${this.dateValue
                    .toLocaleTimeString()
                    .split(':')
                    .slice(0, 2)
                    .join(':')}`;
            }

            return `${this.dateValue.toLocaleDateString()} ${this.dateValue.toLocaleTimeString()}`;
        }

        if (this.type === 'date' && this.dateValue) {
            return this.dateValue.toLocaleDateString();
        }

        return this.value;
    }

    private isDate(value: string): boolean {
        return !isNaN(Date.parse(value));
    }
}
